// src/components/clients/MarkdownEditor.tsx
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import { useEffect } from "react";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Heading2,
  Heading3,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";

interface Props {
  value: string;
  onChange: (markdown: string) => void;
  /** When true, the editor stretches to fill its parent's height (parent must constrain height). */
  fillHeight?: boolean;
  /** Gives the everyday workspace editor a generous writing area without affecting compact editors elsewhere. */
  prominent?: boolean;
  /** Accessible name for the editable writing surface. */
  ariaLabel?: string;
}

export function MarkdownEditor({
  value,
  onChange,
  fillHeight = false,
  prominent = false,
  ariaLabel = "Note editor",
}: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Markdown.configure({ html: false, breaks: true, tightLists: true }),
    ],
    content: value,
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        "aria-multiline": "true",
        role: "textbox",
        class: cn(
          "emery-editor text-base sm:text-sm text-neutral-800 leading-relaxed focus:outline-none",
          "px-4 py-3 bg-white rounded-lg border border-neutral-200",
          "focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2",
          fillHeight
            ? "h-full overflow-y-auto"
            : prominent
              ? "min-h-[50dvh] md:min-h-[58dvh] xl:min-h-[64dvh]"
              : "min-h-[180px]",
        ),
      },
    },
    onUpdate: ({ editor }) => {
      const md =
        (editor.storage as any).markdown?.getMarkdown?.() ?? editor.getHTML();
      onChange(md);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current =
      (editor.storage as any).markdown?.getMarkdown?.() ?? editor.getHTML();
    if (current !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const btn = (active: boolean) =>
    cn(
      "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[4px] transition-colors duration-200 cursor-pointer",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2",
      active
        ? "bg-teal-100 text-teal-800"
        : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900",
    );

  const toolbarButtonProps = (label: string, active: boolean) => ({
    "aria-label": label,
    "aria-pressed": active,
    title: label,
    className: btn(active),
  });

  return (
    <div
      className={cn("space-y-2", fillHeight && "h-full flex flex-col min-h-0")}
    >
      <div
        className="flex items-center gap-1 flex-wrap"
        role="toolbar"
        aria-label="Note formatting"
      >
        <button
          type="button"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          {...toolbarButtonProps(
            "Heading 2",
            editor.isActive("heading", { level: 2 }),
          )}
        >
          <Heading2 size={18} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          {...toolbarButtonProps(
            "Heading 3",
            editor.isActive("heading", { level: 3 }),
          )}
        >
          <Heading3 size={18} aria-hidden="true" />
        </button>
        <span className="w-px h-6 bg-neutral-200 mx-1" aria-hidden="true" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          {...toolbarButtonProps("Bold", editor.isActive("bold"))}
        >
          <Bold size={18} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          {...toolbarButtonProps("Italic", editor.isActive("italic"))}
        >
          <Italic size={18} aria-hidden="true" />
        </button>
        <span className="w-px h-6 bg-neutral-200 mx-1" aria-hidden="true" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          {...toolbarButtonProps(
            "Bulleted list",
            editor.isActive("bulletList"),
          )}
        >
          <List size={18} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          {...toolbarButtonProps(
            "Numbered list",
            editor.isActive("orderedList"),
          )}
        >
          <ListOrdered size={18} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          {...toolbarButtonProps("Quote", editor.isActive("blockquote"))}
        >
          <Quote size={18} aria-hidden="true" />
        </button>
      </div>
      <div className={cn(fillHeight && "flex-1 min-h-0 [&>div]:h-full")}>
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .emery-editor p              { margin: 0.25rem 0; }
        .emery-editor h1             { font-size: 1rem;  font-weight: 600; color: #171717; margin: 0.75rem 0 0.4rem; }
        .emery-editor h2             { font-size: 1rem;  font-weight: 600; color: #171717; margin: 0.75rem 0 0.4rem; }
        .emery-editor h3             { font-size: 0.95rem; font-weight: 600; color: #171717; margin: 0.6rem 0 0.3rem; }
        .emery-editor strong         { font-weight: 600; color: #171717; }
        .emery-editor em             { font-style: italic; }
        .emery-editor ul             { list-style: disc; padding-left: 1.25rem; margin: 0.25rem 0; }
        .emery-editor ol             { list-style: decimal; padding-left: 1.25rem; margin: 0.25rem 0; }
        .emery-editor li             { margin: 0.1rem 0; }
        .emery-editor blockquote     { border-left: 2px solid #5eead4; padding-left: 0.75rem; color: #525252; font-style: italic; margin: 0.5rem 0; }
        .emery-editor code           { background: rgba(255,255,255,0.6); padding: 0.1rem 0.35rem; border-radius: 4px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.9em; }
        .emery-editor hr             { border: 0; border-top: 1px solid #99f6e4; margin: 0.75rem 0; }
        .emery-editor:empty::before  { content: ""; }
      `}</style>
    </div>
  );
}
